"use client";

import { Brain, Heart, Sun, Wind } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheKiraMethod() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const features = [{
    icon: Heart,
    title: "Precision Programming",
    description: "Every workout is meticulously engineered to your specific physiology, ensuring that every movement serves a clear purpose in your long-term athletic development.",
    citation: "Coach Kira Fit Methodology, 2024"
  }, {
    icon: Sun,
    title: "Recovery Phase",
    description: "true performance is built in the recovery phase. We integrate science-backed rest protocols to ensure your nervous system is primed for consistent, high-intensity output.",
    citation: "Performance Science Journal, 2023"
  }, {
    icon: Wind,
    title: "Functional Movement",
    description: "We prioritize biomechanical efficiency, training your body to move with intention and power, reducing injury risk while maximizing functional strength gains.",
    citation: "Kinesiology & Strength Review, 2024"
  }, {
    icon: Brain,
    title: "Cognitive Conditioning",
    description: "Fitness is a mental discipline. Our coaching focuses on building the psychological resilience and focus required to push past plateaus and sustain long-term habits.",
    citation: "Sports Psychology Today, 2023"
  }];
  return <section data-section-id="991" id="the-kira-method" className="dark bg-background text-foreground relative overflow-hidden py-24 md:py-40 px-4 sm:px-6 lg:px-8">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-32 h-[520px] w-[520px] rounded-lg opacity-30 blur-3xl" style={{
      background: "radial-gradient(circle at 30% 30%, hsl(75 100% 50% / 0.2), transparent 70%)"
    }} />

      <div className="relative mx-auto max-w-6xl">
        {}
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
            The Science of Performance
          </p>
          <h2 className="font-sans font-default mt-6 text-4xl sm:text-5xl md:text-6xl font-bold leading-[1.05] tracking-tighter text-foreground">
            The Kira Method: Precision Performance.
          </h2>
          <p className="mt-8 mx-auto max-w-[600px] text-base md:text-lg leading-relaxed text-muted-foreground font-sans">
            We combine rigorous strength training with data-driven recovery protocols. We don't just track reps; we engineer your evolution into a high-performance athlete.
          </p>
        </div>

        {}
        <div className="mt-20 md:mt-24 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          return <FeatureCard key={i} data-index={i} index={i} Icon={Icon} title={feature.title} description={feature.description} citation={feature.citation} />;
        })}
        </div>
      </div>
    </section>;
}
function FeatureCard({
  index,
  Icon,
  title,
  description,
  citation,
  ...rest
}: {
  index: number;
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  description: string;
  citation: string;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} {...rest} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 120}ms`
  }}>
      <Card className="group h-full bg-card text-card-foreground rounded-lg border border-border p-8 md:p-12 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-500 ease-out">
        <div className="flex flex-col gap-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
            <Icon className="h-7 w-7 text-primary" strokeWidth={1.5} />
          </div>

          <h3 className="font-sans font-default text-2xl md:text-[28px] font-semibold leading-tight text-foreground">
            {title}
          </h3>

          <p className="text-sm md:text-[15px] leading-relaxed text-muted-foreground font-sans">
            {description}
          </p>

          <div className="pt-4 mt-auto border-t border-border">
            <p className="font-mono italic text-xs md:text-sm text-muted-foreground/80">
              — {citation}
            </p>
          </div>
        </div>
      </Card>
    </div>;
}